import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, AlertTriangle, Clock } from "lucide-react";

function isWeekday(dateStr) {
  if (!dateStr) return true;
  const d = new Date(dateStr + "T00:00:00");
  const day = d.getDay();
  return day !== 0 && day !== 6;
}

function isBusinessHours(timeStr, dateStr) {
  return isWeekday(dateStr) && timeStr >= "10:00" && timeStr <= "18:00";
}

export default function TimeSelect({ value, onChange, minTime, date, language, triggerClassName, iconColor, showIcon = true }) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const ref = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (listRef.current && listRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && listRef.current && value) {
      const sel = listRef.current.querySelector('[data-selected="true"]');
      if (sel) sel.scrollIntoView({ block: 'center' });
    }
  }, [open]);

  const handleOpen = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropUp = spaceBelow < 280;
      setStyle({
        position: "fixed",
        left: rect.left,
        width: rect.width,
        ...(dropUp
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      });
    }
    setOpen(!open);
  };

  const times = [];
  for (let i = 0; i < 48; i++) {
    const h = String(Math.floor(i / 2)).padStart(2, '0');
    const m = i % 2 === 0 ? '00' : '30';
    const val = `${h}:${m}`;
    if (minTime && val < minTime) continue;
    times.push(val);
  }

  const tooltipText = language === 'en'
    ? "Possible delivery time but with extra costs"
    : "Horário possível de entregar mas com custos extra";

  return (
    <div className="relative w-full" ref={ref}>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleOpen}
        className={`w-full flex items-center gap-2 px-3 py-2.5 font-medium focus:outline-none cursor-pointer ${triggerClassName || ''}`}
      >
        {showIcon && <Clock className="w-4 h-4 shrink-0" style={{ color: iconColor }} />}
        <span className="flex-1 text-left">{value}</span>
        {!isBusinessHours(value, date) && <span title={tooltipText} className="shrink-0"><AlertTriangle className="w-3 h-3 text-[#0a3d62]" /></span>}
        <ChevronDown className={`w-3.5 h-3.5 shrink-0 opacity-50 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && createPortal(
        <div
          ref={listRef}
          className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto"
          style={style}
        >
          {times.map((t) => {
            const green = isBusinessHours(t, date);
            const selected = t === value;
            return (
              <button
                key={t}
                type="button"
                data-selected={selected ? "true" : undefined}
                onClick={() => { onChange(t); setOpen(false); }}
                title={green ? '' : tooltipText}
                className={`w-full flex items-center justify-between px-3 py-2 text-left transition-colors ${selected ? 'bg-[#1e6fd9] hover:bg-[#1e6fd9]' : 'hover:bg-gray-50'}`}
              >
                <span className={`text-sm ${selected ? 'text-white' : 'text-[#0a3d62]'} ${green || selected ? 'font-normal' : 'font-bold'}`}>
                  {t}
                </span>
                {!green && <AlertTriangle className={`w-3 h-3 shrink-0 ${selected ? 'text-white' : 'text-[#0a3d62]'}`} />}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}